// @flow

import React, { Component } from 'react'
import queryString from 'query-string'
import { containerIds } from 'redux/modules/ui'
import urlPaser from 'url'
import MessageManager from 'helpers/MessageManager'
import { actions as accountActions } from 'redux/modules/account'
import { setUIState } from 'redux/modules/ui'
import { amplitudeLogEvent } from 'helpers/amplitude'

export type AccountUseCaseType = {
  checkVerifyEmailIsConfirm: Function,
  inputLoginForm: Function,
  setAccountState: Function,
  configRecaptcha: Function,
  login: Function,
  signUp: Function,
  signOut: Function,
}

type Props = {
  config: {
    URL: string,
    RECAPTCHA_SITE_KEY: string,
  },
  restClient: {},
  location: {
    search: string,
  },
  account: {
    email: string,
    password: string,
    rememberMe: boolean,
    acceptTermOfService: boolean,
  },
  actions: {
    login: Function,
    signUp: Function,
    signOut: Function,
    setAccount: Function,
    clearAccount: Function,
    setUIState: Function,
  },
}

export const AccountUseCaseActionsBridge = Object.assign(
  { setUIState },
  accountActions,
)

export default (WrappedComponent: any) =>
  class AccountUseCase extends Component<Props> {
    // 認証メールのチェック
    checkVerifyEmailIsConfirm = () => {
      const { config } = this.props
      const urlObject = urlPaser.parse(config.URL)
      if (!!urlObject.search) {
        const params = new URLSearchParams(urlObject.search)
        const isConfirm = params.get('isConfirm')
        if (isConfirm) {
          const messageManager = new MessageManager()
          messageManager.success(i18n.t('page.account.l_00035'))
        }
      }
    }

    // accountステート初期処理
    clearAccount = () => {
      const { actions } = this.props
      actions.clearAccount()
    }

    // ログインフォーム入力
    inputLoginForm = (e: SyntheticEvent<*>) => {
      e.preventDefault()
      const { account } = this.props
      if (e.target.name === 'rememberMe') {
        this.setAccountState('rememberMe', !account.rememberMe)
      } else {
        this.setAccountState(e.target.name, e.target.value)
      }
    }

    // accountステートの更新
    setAccountState = (name: string, value: any) => {
      const { actions } = this.props
      const payload = {}
      payload[name] = value
      actions.setAccount(payload)
    }

    // recaptchaの設定
    configRecaptcha = () => {
      const { actions } = this.props
      window.addEventListener('load', () => {
        if (window.grecaptcha) {
          const { config } = this.props
          const { grecaptcha } = window
          grecaptcha.render('google-recaptcha', {
            sitekey: config.RECAPTCHA_SITE_KEY,
            callback: token => this.setAccountState('recaptchaToken', token),
          })
        }
      })
      actions.setUIState(containerIds.SIGN_UP, {
        canSubmit: true,
      })
    }

    // ログイン
    login = (e: Event) => {
      e.preventDefault()

      amplitudeLogEvent('Select Login', { Type: 'Email' })

      const { account, actions, config, restClient, location } = this.props

      const errors = {}
      const email = account.email.trim()
      if (email === '') {
        errors['email'] = i18n.t('page.account.l_00031')
      }
      const password = account.password.trim()
      if (password === '') {
        errors['password'] = i18n.t('page.account.l_00032')
      }

      if (Object.keys(errors).length > 0) {
        actions.setUIState(containerIds.LOGIN, { errors: errors })
        return
      }

      const queries = queryString.parse(location.search)
      actions.login({
        restClient,
        doneUrl: queries.done,
        serviceId: queries.serviceId,
      })
    }

    // IdPログイン
    loginIdP = () => {
      const { actions, restClient, location } = this.props

      const queries = queryString.parse(location.search)
      const serviceId = queries.serviceId
      const done = queryString.parseUrl(queries.done)
      delete queries.done
      delete queries.serviceId
      delete queries.signup
      const doneQueries = queryString.stringify(
        Object.assign(queries, done.query),
      )

      actions.login({
        restClient,
        doneUrl: encodeURIComponent(`${done.url}?${doneQueries}`),
        serviceId,
      })
    }

    // 登録フォーム入力
    inputSignUpForm = (e: SyntheticEvent<*>) => {
      const { account } = this.props
      if (e.target.name === 'acceptTermOfService') {
        this.setAccountState(
          'acceptTermOfService',
          !account.acceptTermOfService,
        )
      } else if (e.target.name === 'acceptTermOfGuest') {
        this.setAccountState('acceptTermOfGuest', !account.acceptTermOfGuest)
      } else {
        this.setAccountState(e.target.name, e.target.value)
      }
    }

    // 登録
    signUp = (e: Event) => {
      e.preventDefault()

      amplitudeLogEvent('Select Signup', { Type: 'Email' })

      const { account, actions, config, restClient } = this.props

      if (account.acceptTermOfService) {
        const parsedUrl = queryString.parseUrl(config.URL)
        const queries = parsedUrl.query
        const doneQuery =
          typeof queries.done === 'string' && queries.done !== 'undefined'
            ? queries.done
            : ''
        const options = {
          done: doneQuery ? encodeURIComponent(doneQuery) : '',
          serviceId: queries.serviceId,
          referralToken: queries.referred_by || queries.referredBy, // queryパラメータ名はreferredByで渡されてくる
        }
        actions.signUp({ restClient, options })
      } else {
        actions.setAccount({
          errors: { accept_term_of_service: i18n.t('page.account.l_00034') },
        })
      }
    }

    // ログアウト
    signOut = () => {
      const { actions, restClient, location } = this.props
      const doneUrl = queryString.parse(location.search).doneUrl
      actions.signOut({ restClient, doneUrl })
    }

    checkErrorMessage = () => {
      const { location } = this.props
      const message = queryString.parse(location.search).message
      if (message) {
        const messageManager = new MessageManager()
        messageManager.warning(message)
      }
    }

    render() {
      return <WrappedComponent {...this.props} accountUseCase={this} />
    }
  }
